/**
 * Wind Query Compute Shader
 *
 * Samples wind data (velocity, speed, direction) at arbitrary query points.
 * Uses wind shader modules for consistent computation with rendering.
 * Supports per-source wind mesh blending via weights.
 *
 * Input:  pointBuffer (storage) - array of vec2<f32> query points
 * Output: resultBuffer (storage) - array of WindQueryResult structs
 */

import {
  ComputeShader,
  type ComputeShaderConfig,
} from "../../../core/graphics/webgpu/ComputeShader";
import type { ShaderModule } from "../../../core/graphics/webgpu/ShaderModule";
import {
  defineUniformStruct,
  f32,
  u32,
} from "../../../core/graphics/UniformStruct";
import {
  WIND_ANGLE_VARIATION,
  WIND_FLOW_CYCLE_PERIOD,
  WIND_NOISE_SPATIAL_SCALE,
  WIND_NOISE_TIME_SCALE,
  WIND_SLOW_TIME_SCALE,
  WIND_SPEED_VARIATION,
  MAX_WIND_SOURCES,
} from "./WindConstants";
import { fn_computeWindAtPoint } from "../shaders/wind.wgsl";
import { fn_lookupWindMeshBlended } from "../shaders/wind-mesh-packed.wgsl";

const WORKGROUP_SIZE = [64, 1, 1] as const;

/**
 * Uniform struct for wind query parameters.
 * Includes per-source weights (8 floats).
 */
export const WindQueryUniforms = defineUniformStruct("Params", {
  pointCount: u32,
  time: f32,
  baseWindX: f32,
  baseWindY: f32,
  influenceSpeedFactor: f32,
  influenceDirectionOffset: f32,
  influenceTurbulence: f32,
  numActiveWindSources: u32,
  // Per-source weights (8 floats, packed as 2 vec4s for alignment)
  weights0: f32,
  weights1: f32,
  weights2: f32,
  weights3: f32,
  weights4: f32,
  weights5: f32,
  weights6: f32,
  weights7: f32,
});

/**
 * Module containing Params and result structs, plus bindings.
 */
const windQueryParamsModule: ShaderModule = {
  preamble: /*wgsl*/ `
// Query parameters
struct Params {
  pointCount: u32,
  time: f32,
  baseWindX: f32,
  baseWindY: f32,
  influenceSpeedFactor: f32,
  influenceDirectionOffset: f32,
  influenceTurbulence: f32,
  numActiveWindSources: u32,
  weights0: f32,
  weights1: f32,
  weights2: f32,
  weights3: f32,
  weights4: f32,
  weights5: f32,
  weights6: f32,
  weights7: f32,
}

// Result structure (matches WindQueryResult interface)
// stride = 4 floats
struct WindQueryResult {
  velocityX: f32,
  velocityY: f32,
  speed: f32,
  direction: f32,
}
  `,
  bindings: {
    params: { type: "uniform", wgslType: "Params" },
    pointBuffer: { type: "storage", wgslType: "array<vec2<f32>>" },
    resultBuffer: { type: "storageRW", wgslType: "array<WindQueryResult>" },
    packedWindMesh: { type: "storage", wgslType: "array<u32>" },
  },
  code: "",
};

/**
 * Module containing the compute entry point.
 */
const windQueryMainModule: ShaderModule = {
  dependencies: [
    fn_computeWindAtPoint,
    fn_lookupWindMeshBlended,
    windQueryParamsModule,
  ],
  code: /*wgsl*/ `
// Wind constants
const WIND_NOISE_SPATIAL_SCALE: f32 = ${WIND_NOISE_SPATIAL_SCALE};
const WIND_NOISE_TIME_SCALE: f32 = ${WIND_NOISE_TIME_SCALE};
const WIND_SPEED_VARIATION: f32 = ${WIND_SPEED_VARIATION};
const WIND_ANGLE_VARIATION: f32 = ${WIND_ANGLE_VARIATION};
const WIND_FLOW_CYCLE_PERIOD: f32 = ${WIND_FLOW_CYCLE_PERIOD};
const WIND_SLOW_TIME_SCALE: f32 = ${WIND_SLOW_TIME_SCALE};

@compute @workgroup_size(${WORKGROUP_SIZE[0]})
fn main(@builtin(global_invocation_id) globalId: vec3<u32>) {
  let index = globalId.x;

  if (index >= params.pointCount) {
    return;
  }

  let queryPoint = pointBuffer[index];

  // Build weights array from uniform fields
  var weights: array<f32, ${MAX_WIND_SOURCES}>;
  weights[0] = params.weights0;
  weights[1] = params.weights1;
  weights[2] = params.weights2;
  weights[3] = params.weights3;
  weights[4] = params.weights4;
  weights[5] = params.weights5;
  weights[6] = params.weights6;
  weights[7] = params.weights7;

  // Look up blended wind mesh for terrain influence
  let meshResult = lookupWindMeshBlended(queryPoint, &packedWindMesh, weights);
  let speedFactor = select(params.influenceSpeedFactor, meshResult.speedFactor, meshResult.found);
  let dirOffset = select(params.influenceDirectionOffset, meshResult.directionOffset, meshResult.found);
  let turb = select(params.influenceTurbulence, meshResult.turbulence, meshResult.found);

  // Compute wind using shared module
  let windResult = computeWindAtPoint(
    queryPoint,
    params.time,
    vec2<f32>(params.baseWindX, params.baseWindY),
    speedFactor,
    dirOffset,
    turb,
    WIND_NOISE_SPATIAL_SCALE,
    WIND_NOISE_TIME_SCALE,
    WIND_SPEED_VARIATION,
    WIND_ANGLE_VARIATION,
    WIND_FLOW_CYCLE_PERIOD,
    WIND_SLOW_TIME_SCALE
  );

  var result: WindQueryResult;
  result.velocityX = windResult.velocity.x;
  result.velocityY = windResult.velocity.y;
  result.speed = windResult.speed;
  result.direction = windResult.direction;

  resultBuffer[index] = result;
}
  `,
};

/**
 * Configuration for the wind query shader.
 */
export const windQueryShaderConfig: ComputeShaderConfig = {
  modules: [windQueryMainModule],
  workgroupSize: WORKGROUP_SIZE,
  label: "WindQueryShader",
};

/**
 * Create a wind query compute shader instance.
 */
export function createWindQueryShader(): ComputeShader {
  return new ComputeShader(windQueryShaderConfig);
}
